import React, { Suspense } from "react";

import { Navigate, useRoutes } from "react-router";

import BasicLayout from "src/components/common/BasicLayout";
import {
  EmailConfirmationType,
  PasswordUpdateType,
} from "src/experiences/auth/constants";
import Login from "src/experiences/auth/MultiAuthFlow/Login";

import Loader from "./components/common/Loader";
import useUser from "./hooks/useUser";

const Logout = React.lazy(() => import("src/experiences/auth/Logout"));
const AzureSSOCallback = React.lazy(
  () => import("src/experiences/auth/MultiAuthFlow/AzureSSOCallback")
);
const InitiateSSOLogin = React.lazy(
  () => import("src/experiences/auth/MultiAuthFlow/InitiateSSOLogin")
);
const McpAuthorize = React.lazy(
  () => import("src/experiences/auth/McpAuthorize")
);
const McpTracebackPage = React.lazy(
  () => import("src/experiences/common/traceback/McpTracebackPage")
);
const EmailConfirmation = React.lazy(
  () => import("src/experiences/auth/MultiAuthFlow/LoginEmailConfirmation")
);
const MfaSetup = React.lazy(
  () => import("src/experiences/auth/MultiAuthFlow/MfaSetup")
);
const MfaVerify = React.lazy(
  () => import("src/experiences/auth/MultiAuthFlow/MfaVerify")
);
const OIDCCallback = React.lazy(
  () => import("src/experiences/auth/MultiAuthFlow/OIDCCallback")
);
const UpdatePassword = React.lazy(
  () => import("src/experiences/auth/MultiAuthFlow/PasswordUpdate")
);
const VerifyEmail = React.lazy(
  () => import("src/experiences/auth/VerifyEmail")
);
const Authors = React.lazy(
  () => import("src/experiences/authors/Orangutan")
);
const CredentialsPage = React.lazy(
  () => import("src/experiences/credentials/CredentialsPage")
);
const DataServices = React.lazy(
  () => import("src/experiences/data-services-experience/DataServices")
);
const DataServicesTaskRenderer = React.lazy(
  () =>
    import("src/experiences/data-services-experience/DataServicesTaskRenderer")
);
const NewDealRedirect = React.lazy(
  () => import("src/experiences/cluster-experience/NewDealRedirect")
);
const ClusterSettingsRedirect = React.lazy(
  () => import("src/experiences/cluster-experience/ClusterSettingsRedirect")
);
const DesignSystemPage = React.lazy(
  () => import("src/experiences/design-system/DesignSystemPage")
);
const SigningPage = React.lazy(
  () => import("src/experiences/signing/SigningPage")
);
const PublicHelpPage = React.lazy(
  () => import("src/experiences/common/documentation/PublicHelpPage")
);
const StaffHelpAdminPage = React.lazy(
  () => import("src/experiences/common/documentation/StaffHelpAdminPage")
);
import UserLandingPage from "src/UserLandingPage";

const suspense = (element: React.ReactNode) => (
  <Suspense fallback={<Loader />}>{element}</Suspense>
);

const RootRoutes = (): React.ReactElement | null => {
  const { user, isStaff, isUserLoading } = useUser();
  const isLoggedIn = Boolean(user);
  const staffHelpAdminElement = isUserLoading ? (
    <Loader />
  ) : isStaff ? (
    suspense(<StaffHelpAdminPage />)
  ) : (
    <Navigate to="/help" replace />
  );

  const routes = [
    // Easter egg
    {
      path: "/authors",
      element: suspense(<Authors />),
      enabled: true,
    },
    // Auth
    {
      path: "/login/*",
      element: <Login />,
      enabled: !isLoggedIn,
    },
    {
      path: "magic-link-confirmation-sent",
      element: suspense(
        <EmailConfirmation
          confirmationType={EmailConfirmationType.MAGIC_LINK}
        />
      ),
      enabled: !isLoggedIn,
    },
    {
      path: "password-setup-confirmation-sent",
      element: suspense(
        <EmailConfirmation
          confirmationType={EmailConfirmationType.PASSWORD_CREATE}
        />
      ),
      enabled: !isLoggedIn,
    },
    {
      path: "password-reset-confirmation-sent",
      element: suspense(
        <EmailConfirmation
          confirmationType={EmailConfirmationType.PASSWORD_RESET}
        />
      ),
      enabled: !isLoggedIn,
    },
    {
      path: "create-password/:userId/:token/:email",
      element: suspense(
        <UpdatePassword updateType={PasswordUpdateType.CREATE} />
      ),
      enabled: !isLoggedIn,
    },
    {
      path: "reset-password/:userId/:token/:email",
      element: suspense(
        <UpdatePassword updateType={PasswordUpdateType.UPDATE} />
      ),
      enabled: !isLoggedIn,
    },
    { path: "mfa-setup", element: suspense(<MfaSetup />), enabled: !isLoggedIn },
    {
      path: "mfa-verify",
      element: suspense(<MfaVerify />),
      enabled: !isLoggedIn,
    },
    {
      path: "/verify-email/:userId/:token/:email",
      element: suspense(<VerifyEmail />),
      enabled: !isLoggedIn,
    },
    {
      path: "/auth/azure-sso-callback",
      element: suspense(<AzureSSOCallback />),
      enabled: !isLoggedIn,
    },
    {
      path: "/auth/oidc/callback",
      element: suspense(<OIDCCallback />),
      enabled: !isLoggedIn,
    },
    {
      path: "/auth/sso/initiate/:clientId",
      element: suspense(<InitiateSSOLogin />),
      enabled: !isLoggedIn,
    },
    {
      path: "/auth/mcp/authorize",
      element: suspense(<McpAuthorize />),
      enabled: true,
    },
    {
      path: "/mcp/traceback",
      element: suspense(<McpTracebackPage />),
      enabled: true,
    },
    {
      path: "/credentials/:token",
      element: suspense(<CredentialsPage />),
      enabled: true,
    },
    {
      path: "/help",
      element: suspense(<PublicHelpPage />),
      enabled: true,
    },
    {
      path: "/help/admin",
      element: staffHelpAdminElement,
      enabled: true,
    },
    {
      path: "/help/admin/new",
      element: staffHelpAdminElement,
      enabled: true,
    },
    {
      path: "/help/admin/articles/:articleId",
      element: staffHelpAdminElement,
      enabled: true,
    },
    {
      path: "/help/:articleSlug",
      element: suspense(<PublicHelpPage />),
      enabled: true,
    },
    {
      path: "/clusters/:clusterId/help",
      element: suspense(<PublicHelpPage />),
      enabled: !isLoggedIn,
    },
    {
      path: "/clusters/:clusterId/help/:articleSlug",
      element: suspense(<PublicHelpPage />),
      enabled: !isLoggedIn,
    },
    {
      path: "/logout",
      element: suspense(<Logout />),
      enabled: true, // Always enabled - logout should work regardless of login state
    },
    {
      path: "/sign/:token",
      element: suspense(<SigningPage />),
      enabled: true, // Always enabled - external signers don't have accounts
    },
    {
      path: "/design-system/*",
      element: suspense(
        <BasicLayout>
          <DesignSystemPage />
        </BasicLayout>
      ),
      enabled: isStaff,
    },
    {
      path: "/data-services/tasks/:taskId/:dataExtractionId/*",
      element: suspense(
        <BasicLayout>
          <DataServicesTaskRenderer rootUrl="/data-services" />
        </BasicLayout>
      ),
      enabled: isStaff,
    },
    {
      path: "/data-services/tasks/:taskId/*",
      element: suspense(
        <BasicLayout>
          <DataServicesTaskRenderer rootUrl="/data-services" />
        </BasicLayout>
      ),
      enabled: isStaff,
    },
    {
      path: "/data-services/*",
      element: suspense(
        <BasicLayout>
          <DataServices rootUrl="/data-services" />
        </BasicLayout>
      ),
      enabled: isStaff,
    },
    {
      path: "/new-deal",
      element: <NewDealRedirect />,
      enabled: isLoggedIn,
    },
    {
      path: "/go/settings",
      element: suspense(<ClusterSettingsRedirect />),
      enabled: isLoggedIn,
    },
    {
      path: "*",
      element: <Navigate to="/login" replace />,
      enabled: !isLoggedIn,
    },
    {
      path: "*",
      // this is essentially a stubbed entity switcher which will handle routing users to the proper experience
      // or allowing them the navigational choice if they are members across multiple entities, entities & clusters.
      // for now it's just going to useEffect to reroute assuming an either/or membership
      element: <UserLandingPage />,
      enabled: isLoggedIn,
    },
  ];

  const nonHiddenRoutes = routes.filter(({ enabled }) => enabled);

  return useRoutes(nonHiddenRoutes);
};

export default RootRoutes;
